import React, { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import * as toast from 'legacy/common/utils/toast';
import { useGetMyMultiTicketsForEvent } from 'legacy/tickets/hooks';
import Loader from 'legacy/common/components/layout/Loader';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import TicketCard from './components/TicketCard';
import MultiTicketsSkeleton from './components/MultiTicketsSkeleton';
import OptionsModal from './components/OptionsModal';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import MultiTierCheckout from 'legacy/events/components/checkout/MultiTierCheckout';
import { EventForUser } from 'legacy/events/types';
import MetaPixel, { MetaPixelRef } from 'legacy/common/components/MetaPixel';

type Props = {
  event: EventForUser;
  onClose: () => void;
  showViewEventButton?: boolean;
  promoterCode?: string;
  discountCode?: string;
  unlockedTier?: { id: string; shareToken: string };
};

const MultiTicketsModal = ({
  onClose,
  event,
  showViewEventButton,
  promoterCode,
  discountCode,
  unlockedTier
}: Props) => {
  const metaPixelRef = useRef<MetaPixelRef>(null);
  const router = useRouter();
  const {
    data,
    isLoading: isLoadingTicket,
    isFetching,
    error,
    refetch: refetchTickets
  } = useGetMyMultiTicketsForEvent(event._id);
  const [isCheckoutVisible, setIsCheckoutVisible] = React.useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const toggleCheckout = () => {
    setIsCheckoutVisible((prevIsVisible) => !prevIsVisible);
  };

  const navigateToEvent = () => {
    router.push(`/events/${event?._id}`);
  };

  if (error) {
    onClose();
    toast.error(error.message);
  }

  return (
    <>
      {event?.metaPixelId && (
        <MetaPixel
          ref={metaPixelRef}
          pixelId={event.metaPixelId}
          initEvent={false}
        />
      )}
      <Modal
        visible
        type={ModalType.SMALL}
        onClose={onClose}
        isBehind={selectedTicket}
        noPadding
      >
        {isLoadingTicket ? (
          <MultiTicketsSkeleton />
        ) : (
          <Loader isOverlay isLoading={isFetching}>
            <div className="h-full">
              <div className="h-full px-5 pb-44 overflow-y-auto">
                <div className="font-bold text-xl pt-7 pb-5">My Tickets</div>
                <div className="flex flex-col gap-5">
                  {data.tickets.map((ticket) => (
                    <TicketCard
                      key={ticket.id}
                      ticket={ticket}
                      setSelectedTicket={setSelectedTicket}
                    />
                  ))}
                </div>
                {data.additionalTickets.length > 0 && (
                  <>
                    <div className="font-bold text-xl pt-7">
                      Transferred Tickets
                    </div>
                    <div className="font-normal text-base pt-2 pb-5 text-gray-500">
                      The tickets you share will appear here. You are able to
                      revoke/reclaim the ticket as your own.
                    </div>
                    <div className="flex flex-col gap-5">
                      {data.additionalTickets.map((ticket) => (
                        <TicketCard
                          isTransfer
                          key={ticket.id}
                          ticket={ticket}
                          setSelectedTicket={setSelectedTicket}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div className="flex flex-col gap-3 bottom-0 absolute left-0 right-0 bg-white p-5 pb-8 shadow-[0_-3px_4px_6px_rgba(111,123,235,0.1)]">
                {showViewEventButton && (
                  <Button onClick={navigateToEvent}>View Event</Button>
                )}
                <Button type={ButtonType.Primary} onClick={toggleCheckout}>
                  Buy More
                </Button>
                {!showViewEventButton && (
                  <Button onClick={onClose}>Close</Button>
                )}
              </div>
            </div>
          </Loader>
        )}
      </Modal>
      {selectedTicket && (
        <OptionsModal
          ticket={selectedTicket}
          refetchTickets={refetchTickets}
          onClose={() => setSelectedTicket(null)}
        />
      )}
      {isCheckoutVisible && (
        <MultiTierCheckout
          event={event}
          metaPixelRef={metaPixelRef}
          onClose={toggleCheckout}
          onSuccess={refetchTickets}
          defaultPromoterCode={promoterCode}
          defaultDiscountCode={discountCode}
          unlockedTier={unlockedTier}
          onViewEventPostCheckout={() => {
            onClose();
            navigateToEvent();
          }}
        />
      )}
    </>
  );
};

export default MultiTicketsModal;
